import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, Play, ChevronRight, ArrowRight, Clock3, Mail,
  Rocket, Lightbulb, Clapperboard, HeartHandshake, Gift, Users,
  ShieldCheck, CalendarCheck, Headphones, ClipboardCheck,
} from "lucide-react";

import babyCaretakerImg    from "../images/stage_01/baby_caretaker.png";
import elderCaretakerImg   from "../images/stage_01/elder_caretaker_R2.png";
import cleaningLabourImg   from "../images/stage_01/cleaning_labour.png";
import rapidCleaningImg    from "../images/stage_01/deep_cleaning.png";
import homeCleaningImg     from "../images/stage_01/home cleaning.png";
import electricianImg      from "../images/stage_01/Electrican_labour.png";
import aboutUsTeamImg      from "../images/stage_01/about_us_team.png";
import caretakerGeneralImg from "../images/stage_01/caretaker_service_general.png";
import gardeningImg        from "../images/stage_01/gardening.png";
import packersMoversImg    from "../images/stage_01/complete_packers_movers.png";
import qasWelcomeImg       from "../images/stage_01/main_welcome.png";
import corporateCleaningImg from "../images/stage_01/corporate_cleaning.png";

const fallbackImage =
  "https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=900&q=80";

// ── Brand colors (ClickNido) ────────────────────────────────────────────────
const TEAL = "#00A4A4";
const NAVY = "#152238";
const BG   = "#F6FAFA";

// Placeholder reference video — a standard public-domain sample clip (Google's
// BigBuckBunny demo file), used ONLY as a stand-in until real customer
// testimonial videos are uploaded. Swap the `video` field on any post below
// for the real file/URL once available — the card markup doesn't need to
// change, it just shows a play icon over the poster image.
const DUMMY_VIDEO_URL = "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4";

const CATEGORY_FILTERS = [
  { key: "all",            label: "All" },
  { key: "stories",        label: "Customer Stories" },
  { key: "launches",       label: "Updates" },
  { key: "tips",           label: "Tips & Guides" },
  { key: "behind-scenes",  label: "Behind the Scenes" },
  { key: "community",      label: "Community & CSR" },
  { key: "promotions",     label: "Promotions" },
];

const FEATURED = {
  lead: {
    title: "How ClickNido helped a family get ready for a big event",
    desc: "A same-day Rapid Cleaning booking ahead of a family function.",
    image: rapidCleaningImg,
    tag: "Customer Story",
    readTime: "4 min read",
  },
  secondary: [
    {
      title: "\"ClickNido saved our weekend\"",
      desc: "Video feedback from a happy customer.",
      poster: babyCaretakerImg,
      video: DUMMY_VIDEO_URL,
      readTime: "3 min read",
    },
    {
      title: "Meet a loyal ClickNido customer",
      desc: "Their unique service experience.",
      image: elderCaretakerImg,
      readTime: "4 min read",
    },
  ],
};

const BLOG_SECTIONS = [
  {
    id: "stories",
    categoryKey: "stories",
    Icon: Users,
    title: "Customer Stories & Testimonials",
    subtitle: "Real feedback and experiences from the families we serve.",
    lead: {
      title: "\"ClickNido saved our weekend\"",
      desc: "Video feedback from a satisfied customer on our Deep Cleaning service.",
      poster: babyCaretakerImg,
      video: DUMMY_VIDEO_URL,
      readTime: "3 min read",
    },
    secondary: [
      {
        title: "A rapid cleaning before a family celebration",
        desc: "Case study from a real family.",
        image: rapidCleaningImg,
        readTime: "4 min read",
      },
      {
        title: "Meet one of our most loyal ClickNido customers",
        desc: "Their story and experience.",
        image: elderCaretakerImg,
        readTime: "4 min read",
      },
    ],
  },
  {
    id: "launches",
    categoryKey: "launches",
    Icon: Rocket,
    title: "Service Launches & Updates",
    subtitle: "New services, product updates and seasonal offers.",
    lead: {
      title: "Introducing Rapid Cleaning for busy days",
      desc: "Our newest service for quick, thorough cleaning ahead of festivals and events.",
      image: rapidCleaningImg,
      tag: "New Service",
      readTime: "4 min read",
    },
    secondary: [
      {
        title: "Faster booking and more service options",
        desc: "What's new in the app.",
        image: qasWelcomeImg,
        tag: "App Update",
        readTime: "3 min read",
      },
      {
        title: "This season's packages and offers",
        desc: "The latest discounts and subscription plans.",
        image: corporateCleaningImg,
        tag: "Offers",
        readTime: "3 min read",
      },
    ],
  },
  {
    id: "tips",
    categoryKey: "tips",
    Icon: Lightbulb,
    title: "Tips & Guides",
    subtitle: "Practical advice for smarter everyday home care.",
    lead: {
      title: "5 easy cleaning habits for a fresher home",
      desc: "Simple, sustainable habits that keep your home spotless with less effort.",
      image: homeCleaningImg,
      tag: "Cleaning",
      readTime: "4 min read",
    },
    secondary: [
      {
        title: "Elderly care at home: everyday safety tips",
        desc: "Fall prevention and daily care advice.",
        image: elderCaretakerImg,
        tag: "Caretaking",
        readTime: "3 min read",
      },
      {
        title: "Basic checks before you call a technician",
        desc: "Simple maintenance checks that can save you a service visit.",
        image: electricianImg,
        tag: "Technical",
        readTime: "3 min read",
      },
    ],
    highlight: {
      title: "Pre-festival home cleaning checklist",
      desc: "A room-by-room checklist to get your home guest-ready.",
    },
  },
  {
    id: "behind-scenes",
    categoryKey: "behind-scenes",
    Icon: Clapperboard,
    title: "Behind the Scenes",
    subtitle: "Meet the people and process behind every ClickNido service.",
    lead: {
      title: "Meet our cleaners, caretakers and technicians",
      desc: "Introducing the verified professionals who show up for you every day.",
      image: cleaningLabourImg,
      tag: "Team",
      readTime: "4 min read",
    },
    secondary: [
      {
        title: "A day in the life of a ClickNido partner",
        desc: "Following a professional through a typical day.",
        image: aboutUsTeamImg,
        tag: "Day in the Life",
        readTime: "4 min read",
      },
      {
        title: "How we train and quality-check every partner",
        desc: "Our onboarding, training and QA process.",
        image: caretakerGeneralImg,
        tag: "Training",
        readTime: "3 min read",
      },
    ],
  },
  {
    id: "community",
    categoryKey: "community",
    Icon: HeartHandshake,
    title: "Community & CSR",
    subtitle: "Our work with local communities and the environment.",
    lead: {
      title: "Supporting elderly care in our community",
      desc: "Our initiatives around cleanliness and elder support drives.",
      image: elderCaretakerImg,
      tag: "Community",
      readTime: "4 min read",
    },
    secondary: [
      {
        title: "Partnering with housing societies and local businesses",
        desc: "How we work with RWAs to bring services to more neighbourhoods.",
        image: packersMoversImg,
        tag: "Partnerships",
        readTime: "3 min read",
      },
      {
        title: "Our sustainability efforts: eco-friendly products, less waste",
        desc: "Steps we're taking toward greener cleaning and less waste.",
        image: gardeningImg,
        tag: "Sustainability",
        readTime: "3 min read",
      },
    ],
  },
  {
    id: "promotions",
    categoryKey: "promotions",
    Icon: Gift,
    title: "Promotions & Engagement",
    subtitle: "Rewards, contests and ways to get involved.",
    lead: {
      title: "Refer a friend, earn rewards",
      desc: "Invite friends and earn exciting rewards together.",
      // No dedicated referral/gift illustration asset exists yet in the
      // project — reusing the general welcome banner as a placeholder.
      image: qasWelcomeImg,
      tag: "Referral",
      readTime: "3 min read",
    },
    secondary: [
      {
        title: "Share your best cleaning tip",
        desc: "Get featured on our blog.",
        image: homeCleaningImg,
        tag: "Contest",
        readTime: "2 min read",
      },
      {
        title: "How well do you know home care?",
        desc: "Take our fun quiz and test your knowledge.",
        image: corporateCleaningImg,
        tag: "Quiz",
        readTime: "2 min read",
      },
    ],
  },
];

const RESPONSIVE_CSS = `
  .cn-blog-hero { display: grid; grid-template-columns: 1.15fr 0.85fr; gap: 28px; align-items: center; }
  .cn-blog-editorial { display: grid; grid-template-columns: 1.3fr 1fr; gap: 16px; align-items: stretch; }
  .cn-blog-small-stack { display: flex; flex-direction: column; gap: 14px; }
  .cn-blog-chips { display: flex; flex-wrap: wrap; gap: 10px; }
  @media (max-width: 900px) {
    .cn-blog-container { padding: 20px 16px 48px !important; }
    .cn-blog-hero { grid-template-columns: 1fr; gap: 16px; }
    .cn-blog-editorial { grid-template-columns: 1fr; }
    .cn-blog-chips { flex-wrap: nowrap; overflow-x: auto; -webkit-overflow-scrolling: touch; padding-bottom: 4px; }
    .cn-blog-chips::-webkit-scrollbar { display: none; }
  }
`;

const BlogPage = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const chipsRef = useRef(null);

  // No existing SEO framework in this app (no react-helmet / document.title
  // usage anywhere in src/ besides HowItWorks.js) — same dependency-free
  // pattern reused here: set a page title + meta description, restored on
  // unmount.
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "ClickNido Blog | Stories, Tips & Updates";
    let meta = document.querySelector('meta[name="description"]');
    const created = !meta;
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    const prevContent = meta.getAttribute("content");
    meta.setAttribute(
      "content",
      "Real stories, useful guides and everything happening around ClickNido — customer stories, service updates, home-care tips and more."
    );
    return () => {
      document.title = prevTitle;
      if (created) meta.remove();
      else if (prevContent !== null) meta.setAttribute("content", prevContent);
    };
  }, []);

  const q = search.trim().toLowerCase();
  const matchesQuery = (post, sectionTitle) => {
    if (!q) return true;
    return (
      post.title.toLowerCase().includes(q) ||
      (post.desc || "").toLowerCase().includes(q) ||
      (post.tag || "").toLowerCase().includes(q) ||
      sectionTitle.toLowerCase().includes(q)
    );
  };

  const visibleSections = BLOG_SECTIONS
    .filter((s) => activeCategory === "all" || s.categoryKey === activeCategory)
    .map((s) => {
      const pool = [s.lead, ...s.secondary].filter((p) => matchesQuery(p, s.title));
      return { ...s, lead: pool[0] || null, secondary: pool.slice(1, 3) };
    })
    .filter((s) => s.lead);

  const featuredMatches =
    matchesQuery(FEATURED.lead, "Featured this week") ||
    FEATURED.secondary.some((p) => matchesQuery(p, "Featured this week"));
  const showFeatured = activeCategory === "all" && featuredMatches;

  const handleViewAll = (key) => {
    setActiveCategory(key);
    chipsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    // TODO: no newsletter subscribe endpoint exists on the backend yet —
    // this acknowledgment is front-end only. Wire this up to a real
    // subscribe API once one is available instead of just flipping local
    // state.
    setSubscribed(true);
  };

  return (
    <div style={styles.page}>
      <style>{RESPONSIVE_CSS}</style>
      <div className="cn-blog-container" style={styles.container}>

        {/* ── Hero ── */}
        <section className="cn-blog-hero" style={styles.hero} aria-label="ClickNido Blog introduction">
          <div style={styles.heroText}>
            <span style={styles.eyebrow}>ClickNido Blog</span>
            <h1 style={styles.heroTitle}>Stories, tips &amp; updates</h1>
            <p style={styles.heroSubtitle}>
              Real stories, useful guides and everything happening around ClickNido.
            </p>
          </div>
          <div style={styles.heroImageWrap}>
            <img
              src={cleaningLabourImg}
              alt="ClickNido professional cleaning a customer's living room"
              style={styles.heroImage}
              onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = fallbackImage; }}
            />
          </div>
        </section>

        {/* ── Search ── */}
        <div style={styles.searchBox}>
          <Search size={18} color="#6b7280" aria-hidden="true" />
          <input
            type="search"
            style={styles.searchInput}
            placeholder="Search articles..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search blog articles"
          />
        </div>

        {/* ── Category filters ── */}
        <div
          ref={chipsRef}
          className="cn-blog-chips"
          style={styles.chipsRow}
          role="group"
          aria-label="Filter articles by category"
        >
          {CATEGORY_FILTERS.map((c) => {
            const active = activeCategory === c.key;
            return (
              <button
                key={c.key}
                type="button"
                aria-pressed={active}
                onClick={() => setActiveCategory(c.key)}
                style={{ ...styles.chip, ...(active ? styles.chipActive : {}) }}
              >
                {c.label}
              </button>
            );
          })}
        </div>

        {/* ── Featured this week ── */}
        {showFeatured && (
          <section style={styles.section} aria-labelledby="featured-heading">
            <div style={styles.sectionHeader}>
              <div>
                <h2 id="featured-heading" style={styles.sectionTitle}>Featured this week</h2>
              </div>
              <button
                type="button"
                style={styles.viewAllBtn}
                onClick={() => chipsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
              >
                View all <ArrowRight size={14} aria-hidden="true" />
              </button>
            </div>
            <div className="cn-blog-editorial" style={styles.editorialGrid}>
              <BlogCard post={FEATURED.lead} variant="lead" />
              <div className="cn-blog-small-stack" style={styles.smallStack}>
                {FEATURED.secondary.map((p) => (
                  <BlogCard key={p.title} post={p} variant="small" />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ── Category sections ── */}
        {visibleSections.length === 0 ? (
          <div style={styles.emptyState}>
            <p>No articles match your search.</p>
          </div>
        ) : (
          visibleSections.map((section) => (
            <BlogSection key={section.id} section={section} onViewAll={handleViewAll} />
          ))
        )}

        {/* ── Newsletter ── */}
        <section style={styles.newsletterCard} aria-label="Newsletter signup">
          <div style={styles.newsletterIcon}><Mail size={20} color="#fff" aria-hidden="true" /></div>
          <div style={styles.newsletterText}>
            <h3 style={styles.newsletterTitle}>Stay in the loop</h3>
            <p style={styles.newsletterDesc}>Home-care tips, ClickNido updates and useful guides — no spam.</p>
          </div>
          <form onSubmit={handleSubscribe} style={styles.newsletterForm}>
            {subscribed ? (
              <span style={styles.subscribedMsg}>Thanks — we'll be in touch!</span>
            ) : (
              <>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  style={styles.newsletterInput}
                  aria-label="Email address"
                />
                <button type="submit" style={styles.newsletterBtn}>Subscribe</button>
              </>
            )}
          </form>
        </section>

        {/* ── Booking CTA ── */}
        <section style={styles.bookingCta} aria-label="Book a ClickNido service">
          <div style={styles.bookingCtaLeft}>
            <h3 style={styles.bookingCtaTitle}>Need a hand at home?</h3>
            <p style={styles.bookingCtaText}>Book trusted ClickNido services in just a few taps.</p>
            <button
              type="button"
              style={styles.bookingCtaBtn}
              onClick={() => navigate("/services/all-services")}
            >
              Book a Service <ArrowRight size={16} aria-hidden="true" />
            </button>
            <ul style={styles.bookingFeatures}>
              <li style={styles.bookingFeatureItem}><ShieldCheck size={16} color={TEAL} aria-hidden="true" /> Trusted Professionals</li>
              <li style={styles.bookingFeatureItem}><CalendarCheck size={16} color={TEAL} aria-hidden="true" /> Easy Booking</li>
              <li style={styles.bookingFeatureItem}><Headphones size={16} color={TEAL} aria-hidden="true" /> Reliable Support</li>
            </ul>
          </div>
          <img
            src={cleaningLabourImg}
            alt="ClickNido professional ready to help"
            style={styles.bookingCtaImage}
            loading="lazy"
            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = fallbackImage; }}
          />
        </section>
      </div>
    </div>
  );
};

// ─── Reusable blog section (1 lead + 2 secondary "editorial" layout) ──────────
const BlogSection = ({ section, onViewAll }) => {
  const { Icon } = section;
  return (
    <section id={section.id} style={styles.section} aria-labelledby={`${section.id}-heading`}>
      <div style={styles.sectionHeader}>
        <div>
          <h2 id={`${section.id}-heading`} style={styles.sectionTitle}>
            <Icon size={19} color={TEAL} aria-hidden="true" /> {section.title}
          </h2>
          <p style={styles.sectionSubtitle}>{section.subtitle}</p>
        </div>
        <button type="button" style={styles.viewAllBtn} onClick={() => onViewAll(section.categoryKey)}>
          View all <ArrowRight size={14} aria-hidden="true" />
        </button>
      </div>

      <div className="cn-blog-editorial" style={styles.editorialGrid}>
        <BlogCard post={section.lead} variant="lead" />
        {section.secondary.length > 0 && (
          <div className="cn-blog-small-stack" style={styles.smallStack}>
            {section.secondary.map((p) => (
              <BlogCard key={p.title} post={p} variant="small" />
            ))}
          </div>
        )}
      </div>

      {section.highlight && (
        <div style={styles.highlightBar}>
          <span style={styles.highlightIcon}><ClipboardCheck size={18} color={TEAL} aria-hidden="true" /></span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={styles.highlightTitle}>{section.highlight.title}</div>
            <div style={styles.highlightDesc}>{section.highlight.desc}</div>
          </div>
          <ChevronRight size={18} color={NAVY} aria-hidden="true" />
        </div>
      )}
    </section>
  );
};

// ─── Reusable article/story card ───────────────────────────────────────────────
const BlogCard = ({ post, variant }) => {
  if (!post) return null;
  const isLead = variant === "lead";
  const isVideo = !!post.video;
  const img = post.image || post.poster;

  const media = (
    <div style={isLead ? styles.leadImgWrap : styles.smallImgWrap}>
      <img
        src={img}
        alt={post.title}
        loading="lazy"
        style={isLead ? styles.leadImg : styles.smallImg}
        onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = fallbackImage; }}
      />
      {post.tag && !isVideo && <span style={styles.cardTag}>{post.tag}</span>}
      {isVideo && (
        <span style={styles.playBadge} aria-hidden="true">
          <span style={styles.playCircle}>
            <Play size={isLead ? 18 : 13} color="#fff" fill="#fff" />
          </span>
        </span>
      )}
    </div>
  );

  const body = (
    <div style={isLead ? styles.leadBody : styles.smallBody}>
      <h3 style={isLead ? styles.leadTitle : styles.smallTitle}>{post.title}</h3>
      {post.desc && <p style={isLead ? styles.leadDesc : styles.smallDesc}>{post.desc}</p>}
      <div style={styles.metaRow}>
        <Clock3 size={12} aria-hidden="true" />
        <span>{post.readTime}</span>
        <ChevronRight size={14} style={{ marginLeft: "auto" }} aria-hidden="true" />
      </div>
    </div>
  );

  if (isVideo) {
    return (
      <a
        href={post.video}
        target="_blank"
        rel="noopener noreferrer"
        style={{ ...(isLead ? styles.leadCard : styles.smallCard), textDecoration: "none", color: "inherit" }}
        aria-label={`Play video: ${post.title}`}
      >
        {media}
        {body}
      </a>
    );
  }

  return (
    <article style={isLead ? styles.leadCard : styles.smallCard}>
      {media}
      {body}
    </article>
  );
};

const styles = {
  page:      { background: BG, minHeight: "100vh", fontFamily: "Arial, sans-serif", color: NAVY },
  container: { maxWidth: "1200px", margin: "0 auto", padding: "28px 40px 60px" },

  hero: { marginBottom: "20px" },
  heroText: { display: "flex", flexDirection: "column", gap: "4px" },
  eyebrow: { color: TEAL, fontWeight: 800, fontSize: "12px", letterSpacing: "1px", textTransform: "uppercase" },
  heroTitle: { margin: "6px 0 8px", fontSize: "32px", fontWeight: 900, color: NAVY, lineHeight: 1.15 },
  heroSubtitle: { margin: 0, fontSize: "14.5px", color: "#4b5563", lineHeight: 1.6, maxWidth: "440px" },
  heroImageWrap: { borderRadius: "20px", overflow: "hidden" },
  heroImage: { width: "100%", height: "100%", maxHeight: "300px", objectFit: "cover", display: "block" },

  searchBox: { display: "flex", alignItems: "center", gap: "10px", background: "#fff", border: "1px solid #e5e7eb", borderRadius: "14px", padding: "12px 16px", margin: "26px 0 16px", maxWidth: "560px" },
  searchInput: { border: "none", outline: "none", flex: 1, fontSize: "14px", background: "transparent", color: NAVY },

  chipsRow: { marginBottom: "30px" },
  chip: { border: "1px solid #e5e7eb", background: "#fff", color: NAVY, fontWeight: 700, fontSize: "13px", padding: "9px 16px", borderRadius: "999px", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 },
  chipActive: { background: TEAL, borderColor: TEAL, color: "#fff" },

  section: { marginBottom: "40px" },
  sectionHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "12px", marginBottom: "16px" },
  sectionTitle: { margin: "0 0 4px", fontSize: "20px", fontWeight: 800, color: NAVY, display: "flex", alignItems: "center", gap: "8px" },
  sectionSubtitle: { margin: 0, fontSize: "13.5px", color: "#6b7280" },
  viewAllBtn: { border: "none", background: "none", color: TEAL, fontWeight: 700, fontSize: "13px", display: "inline-flex", alignItems: "center", gap: "4px", cursor: "pointer", whiteSpace: "nowrap", padding: 0, flexShrink: 0 },

  editorialGrid: {},
  smallStack: {},

  leadCard: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "16px", overflow: "hidden", display: "flex", flexDirection: "column" },
  leadImgWrap: { position: "relative", background: "#000" },
  leadImg: { width: "100%", aspectRatio: "16 / 10", objectFit: "cover", display: "block" },
  leadBody: { padding: "18px" },
  leadTitle: { margin: "0 0 8px", fontSize: "17.5px", fontWeight: 800, color: NAVY, lineHeight: 1.35 },
  leadDesc: { margin: "0 0 10px", fontSize: "13.5px", color: "#6b7280", lineHeight: 1.55 },

  smallCard: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "14px", overflow: "hidden", display: "flex" },
  smallImgWrap: { position: "relative", flexShrink: 0, width: "112px" },
  smallImg: { width: "100%", height: "100%", minHeight: "92px", objectFit: "cover", display: "block" },
  smallBody: { padding: "10px 14px", flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", minWidth: 0 },
  smallTitle: { margin: "0 0 4px", fontSize: "14px", fontWeight: 700, color: NAVY, lineHeight: 1.35 },
  smallDesc: { margin: "0 0 6px", fontSize: "12px", color: "#6b7280", lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" },

  metaRow: { display: "flex", alignItems: "center", gap: "5px", fontSize: "11.5px", color: "#9ca3af", fontWeight: 600 },

  cardTag: { position: "absolute", top: "10px", left: "10px", background: TEAL, color: "#fff", fontSize: "10.5px", fontWeight: 700, padding: "4px 10px", borderRadius: "999px" },
  playBadge: { position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(21,34,56,0.22)" },
  playCircle: { width: "40px", height: "40px", borderRadius: "50%", background: "rgba(21,34,56,0.75)", display: "flex", alignItems: "center", justifyContent: "center" },

  highlightBar: { marginTop: "14px", display: "flex", alignItems: "center", gap: "12px", background: "#E8F7F7", border: "1px solid #c9ecec", borderRadius: "14px", padding: "14px 18px" },
  highlightIcon: { width: "36px", height: "36px", borderRadius: "10px", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  highlightTitle: { fontWeight: 800, fontSize: "14px", color: NAVY },
  highlightDesc: { fontSize: "12.5px", color: "#4b5563", marginTop: "2px" },

  emptyState: { textAlign: "center", padding: "60px 20px", color: "#6b7280" },

  newsletterCard: { display: "flex", alignItems: "center", gap: "16px", background: "#fff", border: "1px solid #e5e7eb", borderRadius: "18px", padding: "22px 26px", marginBottom: "24px", flexWrap: "wrap" },
  newsletterIcon: { width: "42px", height: "42px", borderRadius: "50%", background: TEAL, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  newsletterText: { flex: "1 1 240px" },
  newsletterTitle: { margin: "0 0 4px", fontSize: "17px", fontWeight: 800, color: NAVY },
  newsletterDesc: { margin: 0, fontSize: "13px", color: "#6b7280" },
  newsletterForm: { display: "flex", gap: "10px", flex: "1 1 320px", flexWrap: "wrap" },
  newsletterInput: { flex: "1 1 200px", border: "1px solid #e5e7eb", borderRadius: "10px", padding: "11px 14px", fontSize: "13.5px", outline: "none" },
  newsletterBtn: { border: "none", background: TEAL, color: "#fff", fontWeight: 700, borderRadius: "10px", padding: "0 22px", fontSize: "13.5px", cursor: "pointer" },
  subscribedMsg: { color: TEAL, fontWeight: 700, fontSize: "14px" },

  bookingCta: { display: "flex", alignItems: "center", gap: "24px", background: "#E8F7F7", borderRadius: "22px", padding: "28px 32px", marginBottom: "16px", flexWrap: "wrap" },
  bookingCtaLeft: { flex: "1 1 260px" },
  bookingCtaTitle: { margin: "0 0 6px", fontSize: "21px", fontWeight: 900, color: NAVY },
  bookingCtaText: { margin: "0 0 16px", fontSize: "14px", color: "#4b5563" },
  bookingCtaBtn: { border: "none", background: NAVY, color: "#fff", fontWeight: 700, fontSize: "14px", padding: "13px 24px", borderRadius: "12px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "8px", marginBottom: "18px" },
  bookingFeatures: { listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: "8px", fontSize: "13px", color: NAVY, fontWeight: 600 },
  bookingFeatureItem: { display: "flex", alignItems: "center", gap: "8px" },
  bookingCtaImage: { width: "150px", height: "150px", objectFit: "cover", borderRadius: "18px", flexShrink: 0 },
};

export default BlogPage;
